import AIcon from '@/components/A/AIcon/AIcon'
import FullscreenBtn from '../FullscreenBtn/FullscreenBtn'
import {
  AnalysisTipBlock,
  AnalysisBlockProps,
  AnalysisResultBlock,
  AnalysisResultBlockProps,
  HeatMapBlock,
  TableDataBlock,
  ResultBlock,
  SubjectiveResponseCurveBlock
} from './AnalysisBlock/AnalysisBlock'
import styles from './AnalysisFlowView.module.less'
import { useEffect, useRef, useState } from 'react'
import cls from 'classnames'
import useFlowAnalysisResult from '@/helpers/commonHooks/useFlowAnalysisResults'
import useI18nFlowAnalysisTips from '@/helpers/commonHooks/useFlowAnalysisTips'
import { useTranslation } from 'react-i18next'
import { useMutationObserver, } from 'ahooks'
import { genConsumerAttitudeAnalysisIndicators, genFiveDimensionBenefitsIndicators, genIndexAnalysisIndicators } from './consts'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'


interface Props {
  project: ProjectModels.ProjectDetailV3
  isFullscreen: boolean
  canSwitch?: boolean
  canFullscreen?: boolean
  onClick: (isFullscreen: boolean) => void
  onSwitch: () => void
  onFinish: () => void
}

function getFlowResultSignature(results: Array<AnalysisBlockProps | AnalysisResultBlockProps>) {
  return results.map((item) => {
    if ('status' in item) {
      return [
        'tip',
        item.title,
        item.status,
        item.content.map((content) => `${content.title}:${content.status}`).join('|'),
      ].join('::')
    }
    return ['result', item.title, item.type].join('::')
  }).join('@@')
}

// const useTicker = (interval: number) => {
//   const MAX = 100
//   const [percent, setPercent] = useState<number>(0)
//   useEffect(() => {
//     const timer = setInterval(() => {
//       setPercent((percent) => {
//         if (percent >= MAX) {
//           clearInterval(timer)
//           return MAX
//         }
//         return percent + 1
//       })
//     }, interval)
//     return () => clearInterval(timer)
//   }, [interval])
//   return percent
// }

function AnalysisFlowView(props: Props) {
  const { t, i18n } = useTranslation()
  const FLOW_ANALYSIS_PREFIX = 'dashboard.experience.projectDetailPage'
  const { project, isFullscreen, onClick, onSwitch } = props
  const adDuration = Number(project.adDuration)
  const metricSeconds = project.detailMetrics.length
  const frameSeconds = project.frameInfos.length
  const inferredSecondCount = Math.max(
    metricSeconds,
    frameSeconds,
    Number.isFinite(adDuration) && adDuration > 0 ? Math.ceil(adDuration) : 0
  )
  const isPortraitMode = isPortraitByProjectInfo(project)
  const isSplashCreative =
    isPortraitMode &&
    (project.durationCategory === 'five_to_ten' ||
      (project.durationCategory !== 'ten_plus' &&
        inferredSecondCount > 0 &&
        inferredSecondCount <= 10))
  const contentRef = useRef<HTMLDivElement | null>(null)
  // const percent = useTicker(200)
  // const percent = 100
  const [autoScroll, setAutoScroll] = useState(true)
  const rangeResults = useFlowAnalysisResult(project.progressRatio)
  const { HEATMAP_MEDIA, SUBJECTIVE_RESPONSE_CURVE_MEDIA, INDEX_ANALYSES_MEDIA, CONSUMER_ATTITUDE_ANALYSIS_MEDIA, FIVE_DEMENSION_BENEFITS_MEDIA, FINAL_RESULT_MEDIA } = useI18nFlowAnalysisTips()
  // const [results, setResults] = useState<Array<AnalysisBlockProps | AnalysisResultBlockProps>>([])
  const resultsRef = useRef<Array<AnalysisBlockProps | AnalysisResultBlockProps>>([])
  const resultsSignatureRef = useRef('')
  const [finalResults, setFinalResults] = useState<Array<AnalysisBlockProps | AnalysisResultBlockProps>>([])
  const [shouldDoFlowTicker, setShouldDoFlowTicker] = useState(false)
  const rafIdRef = useRef<number | null>(null)
  const autoScrollRef = useRef(true)
  const mutationRafIdRef = useRef<number | null>(null)
  const [heatmapThumbCollapsed, setHeatmapThumbCollapsed] = useState(true)
  const prevHeatmapThumbCollapsedRef = useRef(heatmapThumbCollapsed)


  const scrollToBottom = (_autoScroll: boolean) => {
    if (!_autoScroll || !contentRef.current) {
      return
    }
    const container = contentRef.current
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      })
    })
  }


  useEffect(() => {
    const results = rangeResults.reduce((acc, cur) => {
      const flowDescs = cur.flowDescs as AnalysisBlockProps[]
      const pass = flowDescs.length > 0 && flowDescs.every(r => { return r.status === 'completed' })
      let resultBlock: AnalysisResultBlockProps[] = []
      const dimensionFactors = project.dimensionFactors
      if (cur.module === 'heatmap' && pass) {
        resultBlock = [{
          ...HEATMAP_MEDIA,
          titleExtra: isPortraitMode ? (
            <div className={styles.collapseBtnBox}>
              <div className={styles.collapseBtn} onClick={() => setHeatmapThumbCollapsed((value) => !value)}>
                <span>
                  {heatmapThumbCollapsed
                    ? t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.thumbExpand`)
                    : t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.thumbCollapse`)
                  }
                </span>
                <AIcon
                  className={cls(styles.collapseIcon, { [styles.collapsed]: heatmapThumbCollapsed })}
                  type="icon-line-global_down"
                  width={12}
                  height={12}
                />
              </div>
            </div>
          ) : undefined,
          content: <HeatMapBlock
            video={project.heatmapResults.heatmapVideo}
            pics={project.heatmapResults.heatmapImages}
            isPortraitMode={isPortraitMode}
            isCollapsed={heatmapThumbCollapsed}
            onExpand={() => setHeatmapThumbCollapsed(false)}
            onToggle={() => setHeatmapThumbCollapsed((value) => !value)} />
        }]
      }
      if (cur.module === 'subjectiveResponseCurve' && pass) {
        resultBlock = [{
          ...SUBJECTIVE_RESPONSE_CURVE_MEDIA,
          content: <SubjectiveResponseCurveBlock project={project} />
        }]
      }
      if (cur.module === 'indexAnalysis' && pass) {
        const indicators = genIndexAnalysisIndicators(dimensionFactors).filter((indicator) => {
          if (!isSplashCreative) {
            return true
          }
          return indicator.i18nKey !== 'dashboard.experience.projectDetailPage.flowAnalysis.terminology.interest'
            && indicator.i18nKey !== 'dashboard.experience.projectDetailPage.flowAnalysis.terminology.ending3Seconds'
        })
        resultBlock = [{
          ...INDEX_ANALYSES_MEDIA,
          content: <TableDataBlock titles={[
            t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.metrics`),
            t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.score`),
            t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.benchmark`),
          ]} indicators={indicators.map(indicator => ({
            label: t(`${indicator.i18nKey}`),
            index: indicator.index,
            norm: indicator.norm,
            trend: indicator.trend
          }))} />
        }]
      }
      if (cur.module === 'consumerAttitudeAnalysis' && pass) {
        const indicators = genConsumerAttitudeAnalysisIndicators(dimensionFactors)
        resultBlock = [
          {
            ...CONSUMER_ATTITUDE_ANALYSIS_MEDIA,
            content: <TableDataBlock titles={[
              t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.attitudePart.attitudeMetrics`),
              t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.score`),
              t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.benchmark`),
            ]} indicators={indicators.map(indicator => ({
              label: t(`${indicator.i18nKey}`),
              index: indicator.index,
              norm: indicator.norm,
              trend: indicator.trend
            }))} />
          },
        ]
      }
      if (cur.module === 'fiveDimension' && pass) {
        const indicators = genFiveDimensionBenefitsIndicators(dimensionFactors)
        resultBlock = [
          {
            ...FIVE_DEMENSION_BENEFITS_MEDIA,
            content: <TableDataBlock titles={[
              t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.dimensionPart.forceDrives`),
              t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.score`),
              t(`${FLOW_ANALYSIS_PREFIX}.flowAnalysis.terminology.benchmark`),
            ]} indicators={indicators.map(indicator => {
              return {
                label: t(`${indicator.i18nKey}`),
                index: indicator.index,
                norm: indicator.norm,
                trend: indicator.trend
              }
            })} />
          },
          {
            ...FINAL_RESULT_MEDIA,
            content: <ResultBlock project={project} />
          }
        ]
      }
      if (resultBlock.length && pass) {
        return [...acc, ...flowDescs, ...resultBlock]
      }
      return [...acc, ...flowDescs]

    }, [] as Array<AnalysisBlockProps | AnalysisResultBlockProps>)
    const nextSignature = getFlowResultSignature(results)
    const heatmapCollapsedChanged = prevHeatmapThumbCollapsedRef.current !== heatmapThumbCollapsed
    resultsRef.current = results
    if (project.progressRatio < 85 || project.progressRatio > 89 || finalResults.length === 0) {
      if (resultsSignatureRef.current !== nextSignature || heatmapCollapsedChanged) {
        resultsSignatureRef.current = nextSignature
        prevHeatmapThumbCollapsedRef.current = heatmapThumbCollapsed
        setFinalResults(resultsRef.current)
      }
    } else {
      setShouldDoFlowTicker(true)
    }
  }, [rangeResults, i18n.language, project, heatmapThumbCollapsed])

  useEffect(() => {
    setHeatmapThumbCollapsed(true)
  }, [project.id])

  useEffect(() => {
    if (!shouldDoFlowTicker) { return }
    if (finalResults.length === resultsRef.current.length) {
      props.onFinish()
    } else {
      /** 鉴于产品想要最后三个数据模块出现的时候，要按顺序一块块的出现，这里拦截一下 results 赋值，
        * 一点点切给 finalResult，重新一点点的输出
        */
      setTimeout(() => {
        const nextResults = resultsRef.current.slice(0, finalResults.length + 1)
        resultsSignatureRef.current = getFlowResultSignature(nextResults)
        setFinalResults(nextResults)
      }, 1000)
    }
  }, [shouldDoFlowTicker, finalResults])

  useEffect(() => {
    const container = contentRef.current
    if (!container) { return }
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const distanceToBottom = scrollHeight - (scrollTop + clientHeight)
      // console.log('distanceToBottom', distanceToBottom)
      // console.log('autoScroll', distanceToBottom <= 75)
      if (distanceToBottom <= 75) {
        setAutoScroll(true)
      } else {
        setAutoScroll(false)
      }
    }
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    autoScrollRef.current = autoScroll
  }, [autoScroll])

  useMutationObserver(
    () => {
      if (mutationRafIdRef.current !== null) {
        return
      }
      mutationRafIdRef.current = window.requestAnimationFrame(() => {
        mutationRafIdRef.current = null
        scrollToBottom(autoScrollRef.current)
      })
    },
    contentRef.current,
    {
      childList: true,
      subtree: true
    }
  )

  useEffect(() => {
    return () => {
      if (mutationRafIdRef.current !== null) {
        window.cancelAnimationFrame(mutationRafIdRef.current)
      }
    }
  }, [])


  return (
    <div className={styles.analysisFlowView} >
      <div className={styles.title}>
        <div className={styles.text}> {t(`${FLOW_ANALYSIS_PREFIX}.title.text1`)}</div>
        {
          project.progressRatio > 85 && (

            <div className={styles.btnBox}>
              {
                isFullscreen && props.canSwitch !== false && (
                  <span className={styles.switchBtn} onClick={onSwitch}>
                    <AIcon type="icon-line-global_swicth" width={16} height={16}></AIcon>
                  </span>
                )
              }
              {props.canFullscreen !== false && (
                <FullscreenBtn isFullscreen={isFullscreen} onClick={onClick} />
              )}
            </div>
          )
        }
      </div>
      <div className={styles.content} ref={contentRef}>
        <div className={styles.analysisBlocks}>
          <div className={styles.analysisBlocksContent}>
            {
              finalResults.map((item, index) => {
                if ('status' in item) {
                  return <AnalysisTipBlock key={index} {...item} />
                } else {
                  return <AnalysisResultBlock key={index} {...item} />
                }
              })
            }
          </div>
        </div>
      </div>
    </div>
  )
}






export default AnalysisFlowView
